/**
 * Demo 模式种子数据。
 *
 * 数据策略：
 * - 贴近真实竞品价格情报的横截面（厂家 x pitch x 区域 x 季度）。
 * - 事实量级 ≈ 40 条，覆盖 7 家竞品、2024Q1 - 2026Q1 共 9 个季度。
 * - 审核台候选 ≈ 8 条，包含高置信度 / 低置信度 / 手工录入三种典型形态。
 * - 解析任务覆盖 queued / running / succeeded / failed / cancelled 全状态。
 * - 所有 source_record / job / candidate / fact 之间的 id 全部对得上，方便跨页跳转。
 */

import type {
  PriceIntelBriefing,
  PriceIntelCandidateFact,
  PriceIntelCompetitorOverview,
  PriceIntelIngestionJob,
  PriceIntelPitchFinderRow,
  PriceIntelPriceFact,
  PriceIntelProductOverview,
  PriceIntelTrendResponse,
} from '../types';

// ---------- 时间 helper ----------
const NOW = new Date('2026-04-20T09:00:00Z').getTime();
const iso = (daysAgo: number, hour = 9, minute = 0) => {
  const base = NOW - daysAgo * 86_400_000;
  const d = new Date(base);
  d.setUTCHours(hour, minute, 0, 0);
  return d.toISOString();
};

// ---------- 竞品与产品维表 ----------
interface CompetitorMeta {
  id: string;
  name: string;
  country_hq: string;
  brand_color: string;
  summary: string;
  pros: string;
  cons: string;
}

const COMPETITORS: CompetitorMeta[] = [
  {
    id: 'comp-absen',
    name: 'Absen',
    country_hq: 'China',
    brand_color: '#E11D48',
    summary: '面向全球的租赁 + 室外固装强势品牌，A25 系列走海外大单。',
    pros: '- 室外 2.5-2.97mm 户外大屏有明显性价比优势\n- DOOH End User 客户资源丰富\n- 本地服务与快速交付能力强',
    cons: '- 小间距 / Fine Pitch 产品线相对弱\n- 高端艺术场景缺少 MIP 标志性型号',
  },
  {
    id: 'comp-leyard',
    name: 'Leyard',
    country_hq: 'China',
    brand_color: '#2563EB',
    summary: 'CarbonLight 租赁矩阵 + DirectLight 控制室方案并进。',
    pros: '- 租赁箱体轻量化和快装结构好\n- 控制室与会议室方案系统完整度高',
    cons: '- 价格在中东 / 俄语区缺乏竞争力\n- 售后反应周期偏长',
  },
  {
    id: 'comp-unilumin',
    name: 'Unilumin',
    country_hq: 'China',
    brand_color: '#16A34A',
    summary: 'Upad 户外旗舰 + Umini Fine Pitch 双线打法。',
    pros: '- Upad 系列户外亮度、箱体可靠性好\n- Umini 小间距在国内会议室市场份额高',
    cons: '- 海外 channel 覆盖不均\n- 高刷电竞场景型号少',
  },
  {
    id: 'comp-roe',
    name: 'ROE Visual',
    country_hq: 'Switzerland',
    brand_color: '#0EA5E9',
    summary: '高端租赁与虚拟拍摄标杆，Verity 与 Ruby 覆盖广播 / 演播厅。',
    pros: '- 顶级广播演播场景标准配置\n- Black Pearl 户外租赁箱体口碑好',
    cons: '- 单价较贵，中端项目难切入\n- 交期长 8-12 周',
  },
  {
    id: 'comp-samsung',
    name: 'Samsung',
    country_hq: 'South Korea',
    brand_color: '#6366F1',
    summary: 'IF 系列瞄准企业总部与会议室 Fine Pitch 市场。',
    pros: '- 品牌认知强，IT 渠道渗透深\n- 整机方案与售后体系完善',
    cons: '- 大尺寸定制能力弱于国产\n- 户外 / 租赁产品线几乎没有',
  },
  {
    id: 'comp-lg',
    name: 'LG',
    country_hq: 'South Korea',
    brand_color: '#A855F7',
    summary: 'MAGNIT + LSCA 组合面向高端会议与指挥中心。',
    pros: '- 0.9-1.3mm 超小间距有技术亮点\n- 整机静音与散热做得好',
    cons: '- 单价在同档次里偏高 10-15%\n- 大屏拼接经验弱',
  },
  {
    id: 'comp-fabulux',
    name: 'Fabulux',
    country_hq: 'Italy',
    brand_color: '#F97316',
    summary: '欧洲本土制造，BIM Plus / BIM Pro 在欧盟项目标配。',
    pros: '- 欧盟 CE / EN 54 合规度高\n- 意大利本地服务响应快',
    cons: '- 出货量不大导致 BOM 成本高\n- 低 pitch 产品型号有限',
  },
];

interface ProductMeta {
  id: string;
  competitor_id: string;
  series: string;
  model: string;
  pitch_mm: number;
  brightness?: number;
  width_mm?: number;
  height_mm?: number;
  indoor_outdoor: 'Indoor' | 'Outdoor';
  application: string;
  pros?: string;
  cons?: string;
}

const MODELS: ProductMeta[] = [
  // Absen
  { id: 'pm-absen-a25', competitor_id: 'comp-absen', series: 'A25', model: 'A25 Outdoor', pitch_mm: 2.5, brightness: 5500, width_mm: 500, height_mm: 500, indoor_outdoor: 'Outdoor', application: 'Rental' },
  { id: 'pm-absen-a27', competitor_id: 'comp-absen', series: 'A27', model: 'A27 Plus', pitch_mm: 2.97, brightness: 5500, width_mm: 500, height_mm: 500, indoor_outdoor: 'Outdoor', application: 'Fixed Install' },
  { id: 'pm-absen-plplusv10', competitor_id: 'comp-absen', series: 'PL', model: 'PL Plus V10', pitch_mm: 2.5, brightness: 1200, indoor_outdoor: 'Indoor', application: 'Rental' },
  { id: 'pm-absen-klv3', competitor_id: 'comp-absen', series: 'KL', model: 'KL V3', pitch_mm: 1.2, brightness: 600, indoor_outdoor: 'Indoor', application: 'Fine Pitch' },
  { id: 'pm-absen-nxii', competitor_id: 'comp-absen', series: 'NX', model: 'NX II 2.6', pitch_mm: 2.6, brightness: 1500, indoor_outdoor: 'Indoor', application: 'Rental' },

  // Leyard
  { id: 'pm-leyard-cli26', competitor_id: 'comp-leyard', series: 'CarbonLight', model: 'CLI 2.6', pitch_mm: 2.6, brightness: 1500, indoor_outdoor: 'Indoor', application: 'Rental' },
  { id: 'pm-leyard-twa15', competitor_id: 'comp-leyard', series: 'TWA', model: 'TWA 1.5', pitch_mm: 1.5, brightness: 800, indoor_outdoor: 'Indoor', application: 'Fine Pitch' },
  { id: 'pm-leyard-dlx19', competitor_id: 'comp-leyard', series: 'DirectLight', model: 'DLX 1.9', pitch_mm: 1.9, brightness: 600, indoor_outdoor: 'Indoor', application: 'Control Room' },
  { id: 'pm-leyard-dls6', competitor_id: 'comp-leyard', series: 'DLS', model: 'DLS Outdoor 6', pitch_mm: 6, brightness: 6500, indoor_outdoor: 'Outdoor', application: 'Rental' },

  // Unilumin
  { id: 'pm-uni-upad4', competitor_id: 'comp-unilumin', series: 'Upad', model: 'Upad IV 3.91', pitch_mm: 3.91, brightness: 5000, indoor_outdoor: 'Outdoor', application: 'Rental' },
  { id: 'pm-uni-uslim26', competitor_id: 'comp-unilumin', series: 'USlim', model: 'USlim II 2.6', pitch_mm: 2.6, brightness: 1500, indoor_outdoor: 'Indoor', application: 'Rental' },
  { id: 'pm-uni-utv15', competitor_id: 'comp-unilumin', series: 'UTV', model: 'UTV III 1.5', pitch_mm: 1.5, brightness: 600, indoor_outdoor: 'Indoor', application: 'Fixed Install' },
  { id: 'pm-uni-umini12', competitor_id: 'comp-unilumin', series: 'Umini', model: 'Umini 1.2', pitch_mm: 1.2, brightness: 600, indoor_outdoor: 'Indoor', application: 'Fine Pitch' },

  // ROE
  { id: 'pm-roe-v26', competitor_id: 'comp-roe', series: 'Verity', model: 'V2.6', pitch_mm: 2.6, brightness: 1200, indoor_outdoor: 'Indoor', application: 'Rental' },
  { id: 'pm-roe-ruby19', competitor_id: 'comp-roe', series: 'Ruby', model: 'Ruby 1.9', pitch_mm: 1.9, brightness: 1200, indoor_outdoor: 'Indoor', application: 'Rental' },
  { id: 'pm-roe-bp28', competitor_id: 'comp-roe', series: 'Black Pearl', model: 'BP 2.8', pitch_mm: 2.8, brightness: 5500, indoor_outdoor: 'Outdoor', application: 'Rental' },

  // Samsung
  { id: 'pm-sam-if15', competitor_id: 'comp-samsung', series: 'IF', model: 'IF 1.5', pitch_mm: 1.5, brightness: 600, indoor_outdoor: 'Indoor', application: 'Fine Pitch' },
  { id: 'pm-sam-if25', competitor_id: 'comp-samsung', series: 'IF', model: 'IF 2.5', pitch_mm: 2.5, brightness: 600, indoor_outdoor: 'Indoor', application: 'Fixed Install' },

  // LG
  { id: 'pm-lg-mag13', competitor_id: 'comp-lg', series: 'MAGNIT', model: 'MAGNIT 1.3', pitch_mm: 1.3, brightness: 600, indoor_outdoor: 'Indoor', application: 'Fine Pitch' },
  { id: 'pm-lg-lsca15', competitor_id: 'comp-lg', series: 'LSCA', model: 'LSCA 1.5', pitch_mm: 1.5, brightness: 600, indoor_outdoor: 'Indoor', application: 'Fine Pitch' },

  // Fabulux
  { id: 'pm-fab-bimplus', competitor_id: 'comp-fabulux', series: 'BIM', model: 'BIM Plus 2.6', pitch_mm: 2.6, brightness: 1200, indoor_outdoor: 'Indoor', application: 'Fixed Install' },
  { id: 'pm-fab-bimpro', competitor_id: 'comp-fabulux', series: 'BIM', model: 'BIM Pro 2.6', pitch_mm: 2.6, brightness: 1500, indoor_outdoor: 'Indoor', application: 'Fixed Install' },
  { id: 'pm-fab-spider', competitor_id: 'comp-fabulux', series: 'Spider', model: 'Spider 2.6', pitch_mm: 2.6, brightness: 1200, indoor_outdoor: 'Indoor', application: 'Rental' },
  { id: 'pm-fab-pilot', competitor_id: 'comp-fabulux', series: 'PilotPro', model: 'PilotPro 2.5', pitch_mm: 2.5, brightness: 1200, indoor_outdoor: 'Indoor', application: 'Fixed Install' },
  { id: 'pm-fab-nt3', competitor_id: 'comp-fabulux', series: 'NT', model: 'NT 3 Outdoor', pitch_mm: 3, brightness: 5000, indoor_outdoor: 'Outdoor', application: 'Rental' },
];

const modelById = (id: string) => {
  const m = MODELS.find((x) => x.id === id);
  if (!m) throw new Error(`Model ${id} not found`);
  return m;
};
const competitorById = (id: string) => {
  const c = COMPETITORS.find((x) => x.id === id);
  if (!c) throw new Error(`Competitor ${id} not found`);
  return c;
};

// ---------- Price Facts 生成 ----------
interface FactInput {
  model_id: string;
  region: string;
  country: string;
  quarter: string;
  per_piece: number;
  per_sqm: number;
  currency?: string;
  daysAgo?: number;
  notes?: string;
  source?: string;
}

let factSeq = 0;
function makeFact(input: FactInput): PriceIntelPriceFact {
  const model = modelById(input.model_id);
  const comp = competitorById(model.competitor_id);
  factSeq += 1;
  const id = `demo-fact-${String(factSeq).padStart(3, '0')}`;
  const daysAgo = input.daysAgo ?? 30;
  return {
    id,
    source_record_id: input.source || 'demo-source-history',
    candidate_fact_id: `demo-candprev-${factSeq}`,
    competitor_id: comp.id,
    product_model_id: model.id,
    competitor: comp.name,
    series: model.series,
    model: model.model,
    pitch_mm: model.pitch_mm,
    brightness: model.brightness ?? null,
    application: model.application,
    indoor_outdoor: model.indoor_outdoor,
    region: input.region,
    country: input.country,
    price_per_piece: input.per_piece,
    price_per_sqm: input.per_sqm,
    currency: input.currency ?? 'USD',
    year_quarter: input.quarter,
    notes: input.notes ?? null,
    confidence: 0.85 + ((factSeq * 7) % 13) / 100,
    source_excerpt: null,
    source_page: null,
    created_at: iso(daysAgo),
    updated_at: iso(daysAgo),
    published_at: iso(daysAgo),
  };
}

// 40 条 facts，横跨 9 个季度 x 7 家竞品
const FACTS: PriceIntelPriceFact[] = [
  // ---- Absen A25 Outdoor Rental 2.5mm：跟真实图片一致的多区域 ----
  makeFact({ model_id: 'pm-absen-a25', region: 'Middle East', country: 'UAE', quarter: '2026Q1', per_piece: 460, per_sqm: 1900, notes: 'DOOH End User，含税未含运', daysAgo: 18 }),
  makeFact({ model_id: 'pm-absen-a25', region: 'North America', country: 'USA', quarter: '2026Q1', per_piece: 475, per_sqm: 1980, notes: 'MSRP $1,881', daysAgo: 22 }),
  makeFact({ model_id: 'pm-absen-a25', region: 'South Europe', country: 'Italy', quarter: '2025Q4', per_piece: 470, per_sqm: 1935, daysAgo: 120, notes: '欧盟 CE 合规' }),
  makeFact({ model_id: 'pm-absen-a25', region: 'South Asia', country: 'Singapore', quarter: '2025Q3', per_piece: 468, per_sqm: 1912, daysAgo: 190 }),
  makeFact({ model_id: 'pm-absen-a25', region: 'China', country: 'China', quarter: '2025Q2', per_piece: 430, per_sqm: 1790, currency: 'USD', daysAgo: 280, notes: '国内市场渠道价' }),
  makeFact({ model_id: 'pm-absen-a25', region: 'RuCIS', country: 'Russia', quarter: '2024Q4', per_piece: 455, per_sqm: 1870, daysAgo: 380 }),

  // ---- Absen A27 Plus 2.97 Outdoor Fixed ----
  makeFact({ model_id: 'pm-absen-a27', region: 'Middle East', country: 'Saudi Arabia', quarter: '2026Q1', per_piece: 540, per_sqm: 2150, daysAgo: 25 }),
  makeFact({ model_id: 'pm-absen-a27', region: 'North America', country: 'USA', quarter: '2025Q4', per_piece: 530, per_sqm: 2080, daysAgo: 130 }),

  // ---- Absen PL Plus V10 2.5 Indoor Rental ----
  makeFact({ model_id: 'pm-absen-plplusv10', region: 'North Europe', country: 'Germany', quarter: '2026Q1', per_piece: 550, per_sqm: 2400, currency: 'EUR', daysAgo: 15 }),
  makeFact({ model_id: 'pm-absen-plplusv10', region: 'East Asia', country: 'Japan', quarter: '2025Q4', per_piece: 582, per_sqm: 2380, daysAgo: 110 }),

  // ---- Absen KL V3 1.2 Fine Pitch ----
  makeFact({ model_id: 'pm-absen-klv3', region: 'China', country: 'China', quarter: '2026Q1', per_piece: 820, per_sqm: 5600, daysAgo: 20, notes: '企业总部订单' }),

  // ---- Absen NX II 2.6 ----
  makeFact({ model_id: 'pm-absen-nxii', region: 'Middle East', country: 'UAE', quarter: '2025Q3', per_piece: 420, per_sqm: 1750, daysAgo: 210 }),

  // ---- Leyard CarbonLight CLI 2.6 Indoor Rental ----
  makeFact({ model_id: 'pm-leyard-cli26', region: 'South Asia', country: 'Singapore', quarter: '2026Q1', per_piece: 380, per_sqm: 2100, daysAgo: 10, notes: '展会现场口头报价' }),
  makeFact({ model_id: 'pm-leyard-cli26', region: 'North Europe', country: 'Germany', quarter: '2025Q4', per_piece: 410, per_sqm: 2180, currency: 'EUR', daysAgo: 130 }),
  makeFact({ model_id: 'pm-leyard-cli26', region: 'North America', country: 'USA', quarter: '2025Q3', per_piece: 395, per_sqm: 2050, daysAgo: 205 }),

  // ---- Leyard TWA 1.5 Fine Pitch ----
  makeFact({ model_id: 'pm-leyard-twa15', region: 'China', country: 'China', quarter: '2026Q1', per_piece: 310, per_sqm: 4200, daysAgo: 32, notes: '国企指挥中心' }),
  makeFact({ model_id: 'pm-leyard-twa15', region: 'North America', country: 'Canada', quarter: '2025Q2', per_piece: 330, per_sqm: 4500, daysAgo: 290 }),

  // ---- Leyard DirectLight DLX 1.9 Control Room ----
  makeFact({ model_id: 'pm-leyard-dlx19', region: 'Middle East', country: 'Qatar', quarter: '2025Q4', per_piece: 520, per_sqm: 3800, daysAgo: 150 }),

  // ---- Leyard DLS Outdoor 6 ----
  makeFact({ model_id: 'pm-leyard-dls6', region: 'South America', country: 'Brazil', quarter: '2025Q2', per_piece: 290, per_sqm: 820, daysAgo: 320, notes: '户外租赁大单' }),

  // ---- Unilumin Upad IV 3.91 Outdoor Rental ----
  makeFact({ model_id: 'pm-uni-upad4', region: 'RuCIS', country: 'Russia', quarter: '2026Q1', per_piece: 280, per_sqm: 1240, daysAgo: 40 }),
  makeFact({ model_id: 'pm-uni-upad4', region: 'South America', country: 'Mexico', quarter: '2025Q3', per_piece: 275, per_sqm: 1210, daysAgo: 220 }),
  makeFact({ model_id: 'pm-uni-upad4', region: 'MEA', country: 'South Africa', quarter: '2025Q1', per_piece: 268, per_sqm: 1180, daysAgo: 400 }),

  // ---- Unilumin USlim II 2.6 ----
  makeFact({ model_id: 'pm-uni-uslim26', region: 'China', country: 'China', quarter: '2026Q1', per_piece: 350, per_sqm: 1680, daysAgo: 15 }),
  makeFact({ model_id: 'pm-uni-uslim26', region: 'South Asia', country: 'Vietnam', quarter: '2025Q4', per_piece: 362, per_sqm: 1720, daysAgo: 140 }),

  // ---- Unilumin UTV III 1.5 ----
  makeFact({ model_id: 'pm-uni-utv15', region: 'China', country: 'China', quarter: '2025Q4', per_piece: 310, per_sqm: 4100, daysAgo: 155 }),

  // ---- Unilumin Umini 1.2 Fine Pitch ----
  makeFact({ model_id: 'pm-uni-umini12', region: 'China', country: 'China', quarter: '2026Q1', per_piece: 680, per_sqm: 5400, daysAgo: 12, notes: '会议室方案标配' }),

  // ---- ROE Verity V2.6 ----
  makeFact({ model_id: 'pm-roe-v26', region: 'North Europe', country: 'Germany', quarter: '2026Q1', per_piece: 520, per_sqm: 2400, currency: 'EUR', daysAgo: 8 }),
  makeFact({ model_id: 'pm-roe-v26', region: 'North America', country: 'USA', quarter: '2025Q4', per_piece: 545, per_sqm: 2480, daysAgo: 125 }),

  // ---- ROE Ruby 1.9 ----
  makeFact({ model_id: 'pm-roe-ruby19', region: 'North America', country: 'USA', quarter: '2026Q1', per_piece: 780, per_sqm: 5100, daysAgo: 28, notes: 'LED Wall for Virtual Production' }),
  makeFact({ model_id: 'pm-roe-ruby19', region: 'East Asia', country: 'South Korea', quarter: '2025Q3', per_piece: 790, per_sqm: 5200, daysAgo: 230 }),

  // ---- ROE Black Pearl 2.8 Outdoor ----
  makeFact({ model_id: 'pm-roe-bp28', region: 'South Europe', country: 'Spain', quarter: '2025Q2', per_piece: 620, per_sqm: 2550, currency: 'EUR', daysAgo: 310 }),

  // ---- Samsung IF 1.5 ----
  makeFact({ model_id: 'pm-sam-if15', region: 'North America', country: 'USA', quarter: '2026Q1', per_piece: 950, per_sqm: 6200, daysAgo: 18 }),
  makeFact({ model_id: 'pm-sam-if15', region: 'East Asia', country: 'South Korea', quarter: '2025Q4', per_piece: 920, per_sqm: 6050, daysAgo: 135 }),

  // ---- Samsung IF 2.5 ----
  makeFact({ model_id: 'pm-sam-if25', region: 'North Europe', country: 'Germany', quarter: '2025Q3', per_piece: 640, per_sqm: 2780, currency: 'EUR', daysAgo: 215 }),

  // ---- LG MAGNIT 1.3 ----
  makeFact({ model_id: 'pm-lg-mag13', region: 'East Asia', country: 'South Korea', quarter: '2026Q1', per_piece: 1120, per_sqm: 6800, daysAgo: 24 }),
  makeFact({ model_id: 'pm-lg-mag13', region: 'North America', country: 'USA', quarter: '2025Q4', per_piece: 1150, per_sqm: 6950, daysAgo: 140 }),

  // ---- LG LSCA 1.5 ----
  makeFact({ model_id: 'pm-lg-lsca15', region: 'North Europe', country: 'UK', quarter: '2025Q2', per_piece: 780, per_sqm: 5300, currency: 'GBP', daysAgo: 300 }),

  // ---- Fabulux BIM Plus 2.6 ----
  makeFact({ model_id: 'pm-fab-bimplus', region: 'South Europe', country: 'Italy', quarter: '2026Q1', per_piece: 288, per_sqm: 1150, currency: 'EUR', daysAgo: 16, notes: '荷兰价，P2.6 价格含税' }),
  makeFact({ model_id: 'pm-fab-bimplus', region: 'North Europe', country: 'France', quarter: '2025Q4', per_piece: 331, per_sqm: 1060, currency: 'EUR', daysAgo: 120 }),

  // ---- Fabulux BIM Pro 2.6 ----
  makeFact({ model_id: 'pm-fab-bimpro', region: 'South Europe', country: 'France', quarter: '2025Q4', per_piece: 321, per_sqm: 1090, currency: 'EUR', daysAgo: 125 }),

  // ---- Fabulux Spider 2.6 ----
  makeFact({ model_id: 'pm-fab-spider', region: 'South Europe', country: 'Italy', quarter: '2025Q3', per_piece: 320, per_sqm: 1280, currency: 'EUR', daysAgo: 220, notes: '4IN1, 铝合金箱体' }),
  makeFact({ model_id: 'pm-fab-spider', region: 'North America', country: 'USA', quarter: '2025Q2', per_piece: 362, per_sqm: 1450, daysAgo: 305 }),

  // ---- Fabulux PilotPro 2.5 ----
  makeFact({ model_id: 'pm-fab-pilot', region: 'South Europe', country: 'Italy', quarter: '2025Q3', per_piece: 400, per_sqm: 1600, currency: 'EUR', daysAgo: 215, notes: 'PSCO 当地价' }),

  // ---- Fabulux NT 3 Outdoor ----
  makeFact({ model_id: 'pm-fab-nt3', region: 'South America', country: 'Argentina', quarter: '2025Q4', per_piece: 425, per_sqm: 850, daysAgo: 130, notes: 'RCV incl.' }),
];

// ---------- 解析任务与候选 ----------
const SOURCES: Record<string, { id: string; kind: 'file' | 'text'; name: string; mime?: string }> = {
  s1: { id: 'demo-src-uae-pdf', kind: 'file', name: '2026Q1 Absen 中东项目报价.pdf', mime: 'application/pdf' },
  s2: { id: 'demo-src-roe-xlsx', kind: 'file', name: 'ROE Verity 2.6mm 报价单.xlsx', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
  s3: { id: 'demo-src-sales-chat', kind: 'text', name: '销售微信聊天摘录' },
  s4: { id: 'demo-src-manual-1', kind: 'text', name: '2026Q2 展会口头报价手工录入' },
  s5: { id: 'demo-src-img-bidfrm', kind: 'file', name: '投标封面扫描件.png', mime: 'image/png' },
  s6: { id: 'demo-src-samsung-mail', kind: 'text', name: 'Samsung 渠道邮件正文' },
  s7: { id: 'demo-src-unilumin-pdf', kind: 'file', name: 'Unilumin Upad 海外报价.pdf', mime: 'application/pdf' },
  s8: { id: 'demo-src-fab-bid', kind: 'file', name: 'Fabulux BIM Plus 意大利投标.pdf', mime: 'application/pdf' },
  s9: { id: 'demo-src-lg-fail', kind: 'file', name: 'LG MAGNIT 内部备忘录.docx' },
};

const JOBS: PriceIntelIngestionJob[] = [
  {
    id: 'demo-job-001',
    source_record_id: SOURCES.s1.id,
    status: 'succeeded',
    job_type: 'parse_extract',
    retry_count: 0,
    parser_pipeline: ['download', 'mistral_ocr', 'llm_extract'],
    result_summary: { source_name: SOURCES.s1.name, candidate_count: 2 },
    claimed_by: 'worker-1',
    claimed_at: iso(12, 8, 50),
    started_at: iso(12, 8, 51),
    finished_at: iso(12, 9, 3),
    created_at: iso(12, 8, 45),
    updated_at: iso(12, 9, 3),
  },
  {
    id: 'demo-job-002',
    source_record_id: SOURCES.s2.id,
    status: 'running',
    job_type: 'parse_extract',
    retry_count: 0,
    parser_pipeline: ['download', 'llm_extract'],
    result_summary: { source_name: SOURCES.s2.name },
    claimed_by: 'worker-1',
    claimed_at: iso(0, 8, 30),
    started_at: iso(0, 8, 31),
    created_at: iso(0, 8, 25),
    updated_at: iso(0, 8, 35),
  },
  {
    id: 'demo-job-003',
    source_record_id: SOURCES.s3.id,
    status: 'succeeded',
    job_type: 'parse_extract',
    retry_count: 0,
    parser_pipeline: ['text_input', 'llm_extract'],
    result_summary: { source_name: SOURCES.s3.name, candidate_count: 1 },
    claimed_at: iso(5, 10),
    finished_at: iso(5, 10, 4),
    created_at: iso(5, 10),
    updated_at: iso(5, 10, 4),
  },
  {
    id: 'demo-job-004',
    source_record_id: SOURCES.s4.id,
    status: 'succeeded',
    job_type: 'manual_entry',
    retry_count: 0,
    parser_pipeline: ['manual_entry'],
    result_summary: { source_name: SOURCES.s4.name, origin: 'manual', candidate_count: 1 },
    finished_at: iso(2, 14, 10),
    created_at: iso(2, 14, 10),
    updated_at: iso(2, 14, 10),
  },
  {
    id: 'demo-job-005',
    source_record_id: SOURCES.s5.id,
    status: 'queued',
    job_type: 'parse_extract',
    retry_count: 0,
    parser_pipeline: ['download', 'mistral_ocr', 'llm_extract'],
    result_summary: { source_name: SOURCES.s5.name },
    created_at: iso(0, 8, 55),
    updated_at: iso(0, 8, 55),
  },
  {
    id: 'demo-job-006',
    source_record_id: SOURCES.s6.id,
    status: 'succeeded',
    job_type: 'parse_extract',
    retry_count: 0,
    parser_pipeline: ['text_input', 'llm_extract'],
    result_summary: { source_name: SOURCES.s6.name, candidate_count: 1 },
    finished_at: iso(3, 11, 20),
    created_at: iso(3, 11, 18),
    updated_at: iso(3, 11, 20),
  },
  {
    id: 'demo-job-007',
    source_record_id: SOURCES.s7.id,
    status: 'succeeded',
    job_type: 'parse_extract',
    retry_count: 1,
    parser_pipeline: ['download', 'mistral_ocr', 'llm_extract'],
    result_summary: { source_name: SOURCES.s7.name, candidate_count: 1, retried_from: 'llm_timeout' },
    finished_at: iso(4, 16, 42),
    created_at: iso(4, 16, 30),
    updated_at: iso(4, 16, 42),
  },
  {
    id: 'demo-job-008',
    source_record_id: SOURCES.s8.id,
    status: 'succeeded',
    job_type: 'parse_extract',
    retry_count: 0,
    parser_pipeline: ['download', 'mistral_ocr', 'llm_extract'],
    result_summary: { source_name: SOURCES.s8.name, candidate_count: 1 },
    finished_at: iso(7, 10, 15),
    created_at: iso(7, 10),
    updated_at: iso(7, 10, 15),
  },
  {
    id: 'demo-job-009',
    source_record_id: SOURCES.s9.id,
    status: 'failed',
    job_type: 'parse_extract',
    retry_count: 2,
    parser_pipeline: ['download', 'mistral_ocr'],
    result_summary: { source_name: SOURCES.s9.name },
    error_message: 'OCR 识别失败：docx 内嵌图片无法提取文本。建议将文件另存为 PDF 后重新上传。',
    finished_at: iso(1, 11, 40),
    created_at: iso(1, 11, 35),
    updated_at: iso(1, 11, 40),
  },
  {
    id: 'demo-job-010',
    source_record_id: 'demo-src-cancelled',
    status: 'cancelled',
    job_type: 'parse_extract',
    retry_count: 0,
    parser_pipeline: ['download'],
    result_summary: { source_name: '测试文件.pdf' },
    error_message: '用户在解析前手动取消。',
    finished_at: iso(6, 15, 22),
    created_at: iso(6, 15, 20),
    updated_at: iso(6, 15, 22),
  },
];

// ---------- 审核台候选（pending） ----------
type CandidateInput = Omit<
  PriceIntelCandidateFact,
  | 'id'
  | 'normalized_payload'
  | 'review_status'
  | 'source_record'
  | 'source_record_id'
  | 'ingestion_job_id'
> & {
  source_key: keyof typeof SOURCES;
  job_id: string;
};

let candSeq = 0;
function makeCandidate(partial: CandidateInput): PriceIntelCandidateFact {
  candSeq += 1;
  const src = SOURCES[partial.source_key];
  const { source_key: _sk, job_id, ...rest } = partial;
  void _sk;
  return {
    id: `demo-cand-${String(candSeq).padStart(3, '0')}`,
    ...rest,
    source_record_id: src.id,
    ingestion_job_id: job_id,
    normalized_payload: { extractor: 'llm' },
    review_status: 'pending_review',
    source_record: { id: src.id, source_name: src.name, source_kind: src.kind },
  };
}

const CANDIDATES: PriceIntelCandidateFact[] = [
  makeCandidate({
    source_key: 's1',
    job_id: 'demo-job-001',
    competitor: 'Absen',
    series: 'A25',
    model: 'A25 Outdoor',
    pitch_mm: 2.5,
    brightness: 5500,
    application: 'Rental',
    indoor_outdoor: 'Outdoor',
    region: 'Middle East',
    country: 'UAE',
    price_per_piece: 460,
    price_per_sqm: 1900,
    currency: 'USD',
    year_quarter: '2026Q1',
    notes: '邮件附件报价，含税未含运。',
    confidence: 0.82,
    source_excerpt: 'Absen A25 Outdoor 2.5mm 对 UAE 项目报价 USD 1,900/sqm, 每片 USD 460，交期 6 周。',
    source_page: 1,
    competitor_id: null,
    product_model_id: null,
    created_at: iso(12, 9, 3),
    updated_at: iso(12, 9, 3),
  }),
  makeCandidate({
    source_key: 's1',
    job_id: 'demo-job-001',
    competitor: 'Absen',
    series: 'A27',
    model: 'A27 Plus',
    pitch_mm: 2.97,
    brightness: 5500,
    application: 'Fixed Install',
    indoor_outdoor: 'Outdoor',
    region: 'Middle East',
    country: 'Saudi Arabia',
    price_per_piece: 540,
    price_per_sqm: 2150,
    currency: 'USD',
    year_quarter: '2026Q1',
    notes: '同一份文件第二部分，沙特项目另报。',
    confidence: 0.78,
    source_excerpt: 'For Riyadh fixed install project, A27 Plus 2.97mm quoted USD 2,150 per sqm ...',
    source_page: 2,
    competitor_id: null,
    product_model_id: null,
    created_at: iso(12, 9, 3),
    updated_at: iso(12, 9, 3),
  }),
  makeCandidate({
    source_key: 's3',
    job_id: 'demo-job-003',
    competitor: 'Leyard',
    series: 'CarbonLight',
    model: 'CLI 2.6',
    pitch_mm: 2.6,
    brightness: 1500,
    application: 'Rental',
    indoor_outdoor: 'Indoor',
    region: 'South Asia',
    country: 'Singapore',
    price_per_piece: 380,
    price_per_sqm: 2100,
    currency: 'USD',
    year_quarter: '2026Q1',
    notes: '展会现场口头报价，待核对。',
    confidence: 0.54,
    source_excerpt: '销售记录："Leyard CarbonLight 2.6 室内固装，大概 2100 USD/sqm，含 2 年质保"',
    source_page: null,
    competitor_id: null,
    product_model_id: null,
    created_at: iso(5, 10, 4),
    updated_at: iso(5, 10, 4),
  }),
  makeCandidate({
    source_key: 's4',
    job_id: 'demo-job-004',
    competitor: 'ROE Visual',
    series: 'Verity',
    model: 'V2.6',
    pitch_mm: 2.6,
    brightness: 1200,
    application: 'Rental',
    indoor_outdoor: 'Indoor',
    region: 'North Europe',
    country: 'Germany',
    price_per_piece: 520,
    price_per_sqm: 2420,
    currency: 'EUR',
    year_quarter: '2026Q1',
    notes: '手工录入：ISE 展会上 ROE 销售主动透露。',
    confidence: 1,
    source_excerpt: null,
    source_page: null,
    competitor_id: null,
    product_model_id: null,
    created_at: iso(2, 14, 10),
    updated_at: iso(2, 14, 10),
  }),
  makeCandidate({
    source_key: 's6',
    job_id: 'demo-job-006',
    competitor: 'Samsung',
    series: 'IF',
    model: 'IF 1.5',
    pitch_mm: 1.5,
    brightness: 600,
    application: 'Fine Pitch',
    indoor_outdoor: 'Indoor',
    region: 'North America',
    country: 'USA',
    price_per_piece: 950,
    price_per_sqm: 6200,
    currency: 'USD',
    year_quarter: '2026Q1',
    notes: '渠道特供价，面向高端企业客户。',
    confidence: 0.91,
    source_excerpt:
      'Samsung IF series — IF 1.5R price list effective 2026-Q1: $6,200/sqm MSRP with 18% channel rebate for enterprise HQ installations.',
    source_page: null,
    competitor_id: null,
    product_model_id: null,
    created_at: iso(3, 11, 20),
    updated_at: iso(3, 11, 20),
  }),
  makeCandidate({
    source_key: 's7',
    job_id: 'demo-job-007',
    competitor: 'Unilumin',
    series: 'Upad',
    model: 'Upad IV 3.91',
    pitch_mm: 3.91,
    brightness: 5000,
    application: 'Rental',
    indoor_outdoor: 'Outdoor',
    region: 'RuCIS',
    country: 'Russia',
    price_per_piece: 280,
    price_per_sqm: 1240,
    currency: 'USD',
    year_quarter: '2026Q1',
    notes: '俄语区本地经销商报价。',
    confidence: 0.73,
    source_excerpt:
      'Upad IV 3.91mm outdoor rental, USD 1,240/sqm, cabinet 500x500mm, shipping to Moscow hub.',
    source_page: 1,
    competitor_id: null,
    product_model_id: null,
    created_at: iso(4, 16, 42),
    updated_at: iso(4, 16, 42),
  }),
  makeCandidate({
    source_key: 's8',
    job_id: 'demo-job-008',
    competitor: 'Fabulux',
    series: 'BIM',
    model: 'BIM Plus 2.6',
    pitch_mm: 2.6,
    brightness: 1200,
    application: 'Fixed Install',
    indoor_outdoor: 'Indoor',
    region: 'South Europe',
    country: 'Italy',
    price_per_piece: 288,
    price_per_sqm: 1150,
    currency: 'EUR',
    year_quarter: '2026Q1',
    notes: '米兰博物馆项目中标价。',
    confidence: 0.88,
    source_excerpt:
      'BIM Plus 2.6 — Museo Nazionale Milano bid, 1,150 EUR/sqm, delivery Q2 2026.',
    source_page: 4,
    competitor_id: null,
    product_model_id: null,
    created_at: iso(7, 10, 15),
    updated_at: iso(7, 10, 15),
  }),
];

// ---------- Trends：从 FACTS 聚合 ----------
function buildTrends(facts: PriceIntelPriceFact[]): PriceIntelTrendResponse {
  const map = new Map<string, Map<string, { sum: number; count: number }>>();
  for (const f of facts) {
    if (!f.competitor || !f.year_quarter || f.price_per_sqm == null) continue;
    const compMap = map.get(f.competitor) ?? new Map();
    map.set(f.competitor, compMap);
    const cur = compMap.get(f.year_quarter) ?? { sum: 0, count: 0 };
    cur.sum += f.price_per_sqm;
    cur.count += 1;
    compMap.set(f.year_quarter, cur);
  }
  const series = Array.from(map.entries()).map(([competitor, points]) => ({
    competitor,
    points: Array.from(points.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([year_quarter, agg]) => ({
        year_quarter,
        avg_price_per_sqm: Math.round(agg.sum / agg.count),
        fact_count: agg.count,
      })),
  }));
  return { series, filters: {} };
}

const TRENDS = buildTrends(FACTS);

// ---------- Briefings ----------
const BRIEFINGS: PriceIntelBriefing[] = [
  {
    id: 'demo-brief-001',
    title: '2026 Q1 竞品价格周报',
    reporting_period: '2026W15',
    filters: {},
    summary_markdown:
      '## 本周核心动向\n\n- **Absen A25 2.5mm 中东**：UAE 项目维持 1,900 USD/sqm，与上周持平。\n- **ROE Verity V2.6 德国**：2,400 EUR/sqm 稳定，LSCA 项目预计 Q2 集中采购。\n- **Samsung IF 1.5**：美国渠道价涨至 6,200 USD/sqm（+2.5% QoQ）。\n- **Fabulux BIM Plus**：意大利米兰博物馆中标 1,150 EUR/sqm。\n\n## 建议\n\n- 中东大单与 Absen 竞争，建议跟进 A27 Plus 做差异化。\n- Fine Pitch 高端会议市场，Samsung/LG 价差 ~15%，可重点争取。',
    chart_payload: {},
    created_at: iso(1, 9),
    updated_at: iso(1, 9),
  },
  {
    id: 'demo-brief-002',
    title: '2026 Q1 小间距专题（1.2-1.9mm）',
    reporting_period: '2026Q1',
    filters: { pitch_range: '1.2-1.9' },
    summary_markdown:
      '## 小间距市场速览\n\n| 厂家 | 型号 | pitch | 代表区域 | Price/sqm |\n|---|---|---|---|---|\n| LG | MAGNIT 1.3 | 1.3mm | USA | 6,950 USD |\n| Samsung | IF 1.5 | 1.5mm | USA | 6,200 USD |\n| Unilumin | Umini 1.2 | 1.2mm | China | 5,400 USD |\n| ROE | Ruby 1.9 | 1.9mm | USA | 5,100 USD |\n| Leyard | TWA 1.5 | 1.5mm | China | 4,200 USD |\n\n## 结论\n\n- 1.3-1.5mm 区间 LG / Samsung 占据高端价位（>6000 USD/sqm）\n- 1.2mm Fine Pitch 国产品牌比韩系便宜 20-30%',
    chart_payload: {},
    created_at: iso(8, 9),
    updated_at: iso(8, 9),
  },
  {
    id: 'demo-brief-003',
    title: '2025 全年竞品趋势回顾',
    reporting_period: '2025',
    filters: {},
    summary_markdown:
      '## 2025 全年观察\n\n- 全球 LED 显示屏价格**整体下行 3-6%**，中端户外下跌最明显。\n- **中东、东欧**成为中国品牌主要增量市场（Absen / Unilumin 增速最快）。\n- **欧洲 Fine Pitch** 市场由 Samsung / LG / Fabulux 三分天下。\n\n## 2026 展望\n\n- 预计小间距 Fine Pitch 持续降价 3-5%\n- Virtual Production 对 1.9-2.8mm 高刷产品需求回暖',
    chart_payload: {},
    created_at: iso(60, 9),
    updated_at: iso(60, 9),
  },
];

// ---------- 派生给 views 用的聚合数据 ----------
function buildCompetitorOverview(): PriceIntelCompetitorOverview[] {
  return COMPETITORS.map((c) => {
    const compFacts = FACTS.filter((f) => f.competitor_id === c.id);
    const compModels = MODELS.filter((m) => m.competitor_id === c.id);
    const prices = compFacts.map((f) => f.price_per_sqm || 0).filter((v) => v > 0);
    const regions = Array.from(new Set(compFacts.map((f) => f.region).filter(Boolean))) as string[];
    const countries = Array.from(new Set(compFacts.map((f) => f.country).filter(Boolean))) as string[];
    const latest = compFacts
      .map((f) => f.published_at)
      .filter(Boolean)
      .sort()
      .pop();
    return {
      competitor_id: c.id,
      competitor_name: c.name,
      country_hq: c.country_hq,
      brand_color: c.brand_color,
      logo_storage_key: null,
      summary_markdown: c.summary,
      pros_markdown: c.pros,
      cons_markdown: c.cons,
      product_count: compModels.length,
      series_count: new Set(compModels.map((m) => m.series)).size,
      fact_count: compFacts.length,
      min_price_per_sqm: prices.length ? Math.min(...prices) : null,
      max_price_per_sqm: prices.length ? Math.max(...prices) : null,
      avg_price_per_sqm: prices.length ? Math.round(prices.reduce((a, b) => a + b, 0) / prices.length) : null,
      latest_published_at: latest ?? null,
      regions,
      countries,
    };
  });
}

function buildProductOverview(): PriceIntelProductOverview[] {
  return MODELS.map((m) => {
    const comp = competitorById(m.competitor_id);
    const modelFacts = FACTS.filter((f) => f.product_model_id === m.id);
    const sqms = modelFacts.map((f) => f.price_per_sqm || 0).filter((v) => v > 0);
    const pieces = modelFacts.map((f) => f.price_per_piece || 0).filter((v) => v > 0);
    const latestQuarter = modelFacts
      .map((f) => f.year_quarter)
      .filter(Boolean)
      .sort()
      .pop();
    const latestPublished = modelFacts
      .map((f) => f.published_at)
      .filter(Boolean)
      .sort()
      .pop();
    return {
      product_model_id: m.id,
      competitor_id: m.competitor_id,
      competitor_name: comp.name,
      series: m.series,
      model: m.model,
      pitch_mm: m.pitch_mm,
      brightness: m.brightness ?? null,
      width_mm: m.width_mm ?? null,
      height_mm: m.height_mm ?? null,
      indoor_outdoor: m.indoor_outdoor,
      application: m.application,
      fact_count: modelFacts.length,
      min_price_per_sqm: sqms.length ? Math.min(...sqms) : null,
      max_price_per_sqm: sqms.length ? Math.max(...sqms) : null,
      avg_price_per_sqm: sqms.length ? Math.round(sqms.reduce((a, b) => a + b, 0) / sqms.length) : null,
      min_price_per_piece: pieces.length ? Math.min(...pieces) : null,
      max_price_per_piece: pieces.length ? Math.max(...pieces) : null,
      latest_quarter: latestQuarter ?? null,
      latest_published_at: latestPublished ?? null,
      pros_markdown: m.pros ?? null,
      cons_markdown: m.cons ?? null,
      image_count: 0,
    };
  });
}

function buildPitchFinder(): PriceIntelPitchFinderRow[] {
  return MODELS.map((m) => {
    const comp = competitorById(m.competitor_id);
    const modelFacts = FACTS.filter((f) => f.product_model_id === m.id);
    const sqms = modelFacts.map((f) => f.price_per_sqm || 0).filter((v) => v > 0);
    const latestQuarter = modelFacts
      .map((f) => f.year_quarter)
      .filter(Boolean)
      .sort()
      .pop();
    const sample = modelFacts[0];
    return {
      product_model_id: m.id,
      competitor_id: m.competitor_id,
      competitor_name: comp.name,
      series: m.series,
      model: m.model,
      pitch_mm: m.pitch_mm,
      brightness: m.brightness ?? null,
      width_mm: m.width_mm ?? null,
      height_mm: m.height_mm ?? null,
      indoor_outdoor: m.indoor_outdoor,
      application: m.application,
      fact_count: modelFacts.length,
      avg_price_per_sqm: sqms.length ? Math.round(sqms.reduce((a, b) => a + b, 0) / sqms.length) : null,
      min_price_per_sqm: sqms.length ? Math.min(...sqms) : null,
      max_price_per_sqm: sqms.length ? Math.max(...sqms) : null,
      latest_quarter: latestQuarter ?? null,
      sample_region: sample?.region ?? null,
      sample_country: sample?.country ?? null,
      sample_notes: sample?.notes ?? null,
    };
  });
}

// ---------- 导出 ----------
const demoSeed = {
  jobs: JOBS,
  candidates: CANDIDATES,
  price_facts: FACTS,
  briefings: BRIEFINGS,
  trends: TRENDS,
  competitor_overviews: buildCompetitorOverview(),
  product_overviews: buildProductOverview(),
  pitch_finder: buildPitchFinder(),
};

export default demoSeed;
